import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BANCO_SYNC_HABILITADO,
  dispararBoletosVencimento,
  fetchStatusBoletosVencimento,
} from '../../../api/bancoSync/bancoSyncApi';
import type { JobCompleto } from '../../../components/sync-painel/types';
import EtapasTimeline from '../../../components/sync-painel/EtapasTimeline';
import ModalSync from '../../../components/sync-painel/ModalSync';
import { classeStatus, duracaoLegivel, formatarDataHora, rotuloStatus } from '../../../components/sync-painel/statusBadge';
import '../../../shared/ui/badge-status.css';
import { notify } from '../../../shared/feedback/notify';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import './SincronizarBoletosButton.css';

/**
 * Botão "Sincronizar boletos" / "Acompanhar sincronização" (comissionamentos/at-01).
 *
 * Dispara a varredura de boletos do SGA por data de VENCIMENTO — mês anterior + mês corrente —
 * e, **enquanto ela roda, vira acompanhamento** (pedido do Victor, 03/09/2026): o rótulo muda e o
 * modal deixa de perguntar "quer sincronizar?" para mostrar em que pé está.
 *
 * As duas chamadas vão **direto ao backend do `banco-de-dados-atual`**, levando o JWT do SOAC; lá
 * as rotas aceitam ADMIN, TI e COMISSIONAMENTO.
 *
 * ⚠️ **Nenhum componente de acompanhamento foi criado aqui**: o corpo do modal é o mesmo
 * `EtapasTimeline` + `statusBadge` do painel de Sincronização da Base, e o casco é o `ModalSync`
 * — as peças já estavam em `components/sync-painel/`, extraídas quando o painel da FIPE precisou
 * delas. Os dois backends expõem o mesmo shape de job de propósito, e é o que permite isso.
 *
 * ⚠️ **Gate por env**: sem `REACT_APP_BANCO_DADOS_URL` o botão aparece DESABILITADO com
 * explicação, em vez de sumir. O carimbo "Dados atualizados em" não depende dessa env (passa
 * pelo backend do SOAC), então a informação continua na tela mesmo onde a ação não existe.
 */

/** Enquanto o modal de acompanhamento está aberto, o estado precisa parecer vivo. */
const POLL_MODAL_ABERTO_MS = 5000;
/** Com o modal fechado, é só para saber se o rótulo do botão deve mudar. */
const POLL_FUNDO_MS = 20000;

interface Props {
  /** Chamado quando o disparo dá certo e quando a sincronização termina, para o carimbo reconsultar. */
  onDisparado?: () => void;
}

const SincronizarBoletosButton: React.FC<Props> = ({ onDisparado }) => {
  const [confirmando, setConfirmando] = useState(false);
  const [acompanhando, setAcompanhando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [job, setJob] = useState<JobCompleto | null>(null);
  const [erroStatus, setErroStatus] = useState(false);

  const emAndamento = job?.status === 'RUNNING';
  // Guarda o estado anterior para avisar o carimbo QUANDO a sincronização termina — sem isto o
  // carimbo continuaria com a data antiga até alguém recarregar a página.
  const estavaRodando = useRef(false);

  const consultar = useCallback(async () => {
    if (!BANCO_SYNC_HABILITADO) return;
    try {
      const atual = await fetchStatusBoletosVencimento();
      setJob(atual);
      setErroStatus(false);
      if (estavaRodando.current && atual?.status !== 'RUNNING') onDisparado?.();
      estavaRodando.current = atual?.status === 'RUNNING';
    } catch {
      // ⚠️ NÃO zera o job: um erro de consulta não é prova de que a sincronização parou. Zerar
      // faria o botão voltar de "Acompanhar" para "Sincronizar" no meio de uma execução, e o
      // clique seguinte morreria num 409 sem que o usuário entendesse o que aconteceu.
      setErroStatus(true);
    }
  }, [onDisparado]);

  useEffect(() => {
    consultar();
  }, [consultar]);

  useEffect(() => {
    if (!BANCO_SYNC_HABILITADO) return undefined;
    // Só continua consultando enquanto há algo a observar: terminada a execução e com o modal
    // fechado, parar de bater no backend do banco a cada 20s.
    if (!emAndamento && !acompanhando) return undefined;
    const intervalo = acompanhando ? POLL_MODAL_ABERTO_MS : POLL_FUNDO_MS;
    const timer = setInterval(consultar, intervalo);
    return () => clearInterval(timer);
  }, [consultar, emAndamento, acompanhando]);

  const disparar = async () => {
    setConfirmando(false);
    setEnviando(true);
    try {
      const resposta = await dispararBoletosVencimento();
      // 409: outro sync do SGA está rodando. Não é erro de quem clicou nem falha do sistema —
      // é o lock funcionando. Dizer "não foi possível sincronizar" mandaria abrir chamado por um
      // comportamento correto.
      if ((resposta as { conflito?: unknown }).conflito) {
        notify.warning(
          'Já existe uma sincronização em andamento no banco de dados. A varredura será feita ' +
            'assim que ela terminar, ou você pode tentar de novo em alguns minutos.',
          { toastId: 'sync-boletos-conflito' },
        );
      } else {
        notify.success(
          'Sincronização iniciada. Acompanhe o andamento pelo botão, que passou a mostrar o progresso.',
        );
        // Abre o acompanhamento já: quem clicou quer ver acontecer.
        setAcompanhando(true);
      }
      await consultar();
      onDisparado?.();
    } catch (error) {
      // Nunca mostrar sucesso para algo que falhou (at-60).
      //
      // ⚠️ Texto à mão em vez de `actionError`: o helper conjuga o verbo no MASCULINO SINGULAR a
      // partir do `action`, e "sincronização" é feminino — sairia "sincronização de boletos
      // iniciado" (a armadilha do BUG-V51). `errorFrom` prioriza a mensagem do backend, que aqui
      // é o que interessa: um 403 diz que o papel não alcança a rota, e um erro de rede que o
      // serviço de sincronização está fora.
      notify.errorFrom(error, 'Não foi possível iniciar a sincronização de boletos.');
    } finally {
      setEnviando(false);
    }
  };

  if (!BANCO_SYNC_HABILITADO) {
    return (
      <button
        type="button"
        className="comis-sync-btn"
        disabled
        title="A sincronização não está configurada neste ambiente. Acione a TI."
      >
        Sincronizar boletos
      </button>
    );
  }

  const etapas = job?.resultado?.etapas ?? [];
  const parametros = job?.resultado?.parametros as { dataInicial?: string; dataFinal?: string } | undefined;

  return (
    <>
      <button
        type="button"
        className="comis-sync-btn"
        onClick={() => (emAndamento ? setAcompanhando(true) : setConfirmando(true))}
        disabled={enviando}
      >
        {enviando ? 'Iniciando...' : emAndamento ? 'Acompanhar sincronização' : 'Sincronizar boletos'}
      </button>

      {confirmando && (
        <ConfirmModal
          isOpen
          title="Sincronizar boletos do SGA"
          message={
            'Vai buscar no SGA os boletos com vencimento no mês passado e no mês atual, e atualizar ' +
            'a base que os comissionamentos usam. Cobre apenas a base RJ, que é onde os ' +
            'comissionamentos operam hoje.\n\n' +
            'A varredura roda em segundo plano e costuma levar alguns minutos — você pode continuar ' +
            'usando o sistema. Se já houver outra sincronização em andamento, esta é adiada.\n\n' +
            'Isto acontece sozinho todo dia às 04h; use aqui quando precisar do dado antes disso.'
          }
          confirmLabel="Sincronizar agora"
          onConfirm={disparar}
          onCancel={() => setConfirmando(false)}
        />
      )}

      {acompanhando && (
        <ModalSync
          titulo="Sincronização de boletos do SGA"
          subtitulo={
            parametros?.dataInicial && parametros?.dataFinal
              ? `Vencimento de ${parametros.dataInicial} a ${parametros.dataFinal}`
              : undefined
          }
          onClose={() => setAcompanhando(false)}
          largura="min(760px, 94vw)"
        >
          {!job ? (
            <p className="comis-sync-modal-vazio">Nenhuma sincronização executada ainda.</p>
          ) : (
            <>
              <div className="comis-sync-modal-linha">
                {/* `classeStatus` já devolve `cilia-badge ...` — prefixar de novo duplicava a classe. */}
                <span className={classeStatus(job.status)}>{rotuloStatus(job.status)}</span>
                <span>
                  Início: <strong>{formatarDataHora(job.startedAt)}</strong>
                </span>
                {job.finishedAt && (
                  <span>
                    Fim: <strong>{formatarDataHora(job.finishedAt)}</strong>
                  </span>
                )}
                {job.finishedAt && duracaoLegivel(job.startedAt, job.finishedAt) && (
                  <span>
                    Duração: <strong>{duracaoLegivel(job.startedAt, job.finishedAt)}</strong>
                  </span>
                )}
              </div>

              {/* ⚠️ Falha na CONSULTA do status não pode passar por "a sincronização parou": o que
                  está na tela é a última leitura boa, e isso precisa estar dito. */}
              {erroStatus && (
                <p className="comis-sync-modal-aviso" role="alert">
                  Não foi possível atualizar o andamento agora — o que aparece abaixo é a última
                  informação obtida. A sincronização pode estar seguindo normalmente.
                </p>
              )}

              {/* Não repete o rótulo do badge logo acima — acrescenta o que ele não diz: que dá
                  para fechar sem interromper nada, e que a tela se atualiza sozinha. */}
              {job.status === 'RUNNING' && (
                <p className="comis-sync-modal-vivo">
                  Esta tela se atualiza sozinha. Você pode fechá-la e continuar usando o sistema —
                  a sincronização roda no servidor e não depende desta janela.
                </p>
              )}

              {etapas.length > 0 ? (
                <EtapasTimeline etapas={etapas} />
              ) : (
                <p className="comis-sync-modal-vazio">
                  {job.status === 'RUNNING'
                    ? 'A primeira etapa ainda não reportou progresso.'
                    : 'Esta execução não registrou etapas.'}
                </p>
              )}
            </>
          )}
        </ModalSync>
      )}
    </>
  );
};

export default SincronizarBoletosButton;
